import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, CreditCard, PlusCircle, Settings, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePermission } from '@/hooks/use-permission';
import { useAuth } from '@/lib/auth-context';

interface NavItem {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  permission?: string;
}

const navItems: NavItem[] = [
  { href: '/', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/payments', label: 'Payments', icon: CreditCard },
  { href: '/payments/new', label: 'Create Payment', icon: PlusCircle, permission: 'paynow:payments:create' },
  { href: '/settings', label: 'Settings', icon: Settings, permission: 'paynow:settings:view' },
];

export function Sidebar() {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();

  return (
    <aside className="fixed left-0 top-0 flex h-screen w-60 flex-col bg-slate-900 text-white">
      <div className="flex h-14 items-center border-b border-slate-700 px-5">
        <span className="text-sm font-bold tracking-tight">Klik PayNow</span>
      </div>

      <nav className="flex-1 space-y-0.5 px-3 py-4">
        {navItems.map((item) => (
          <SidebarItem key={item.href} item={item} pathname={pathname} />
        ))}
      </nav>

      <div className="border-t border-slate-700 px-3 py-3">
        {user && (
          <p className="mb-2 truncate px-3 text-xs text-slate-400">{user.email}</p>
        )}
        <button
          onClick={logout}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-slate-400 transition-colors hover:bg-slate-800 hover:text-white"
        >
          <LogOut className="h-4 w-4 shrink-0" />
          Sign out
        </button>
      </div>
    </aside>
  );
}

function SidebarItem({ item, pathname }: { item: NavItem; pathname: string }) {
  const hasPermission = usePermission(item.permission ?? '');

  if (item.permission && !hasPermission) return null;

  const isActive = item.href === '/' ? pathname === '/' : pathname.startsWith(item.href);

  return (
    <Link
      to={item.href}
      className={cn(
        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
        isActive
          ? 'bg-slate-700 text-white'
          : 'text-slate-400 hover:bg-slate-800 hover:text-white',
      )}
    >
      <item.icon className="h-4 w-4 shrink-0" />
      {item.label}
    </Link>
  );
}
